import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Trophy, Brain, BarChart3, ShieldCheck, Flame, Star,
  ArrowRight, Check, Coins, Target, Rocket, BookOpen, Calculator, Zap, Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Typewriter } from "../components/Typewriter";

const PARENT_IMG = "https://images.unsplash.com/photo-1679539143915-947178dbea00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNoaWxkJTIwcGxheWluZyUyMHRhYmxldCUyMGRhcmslMjBsaWdodGluZ3xlbnwwfHx8fDE3ODE1NjY0NTh8MA&ixlib=rb-4.1.0&q=85";

const C = { cyan: "#00FFD1", magenta: "#FF2EA6", indigo: "#6366F1", green: "#39FF14" };

function NeonLogo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center box-glow-cyan group-hover:rotate-6 transition-transform" style={{ background: "linear-gradient(135deg,#00FFD1,#6366F1)" }}>
        <BookOpen className="w-5 h-5 text-black" strokeWidth={2.5} />
      </div>
      <span className="font-display font-extrabold text-lg text-white">Slim<span className="neon-gradient-text">Avontuur</span></span>
    </Link>
  );
}

const features = [
  { icon: Brain, title: "Slimme teksten", desc: "Elke oefening is uniek en groeit mee met jouw kind.", glow: C.cyan },
  { icon: Calculator, title: "Lezen én rekenen", desc: "Begrijpend lezen en rekenen in één spannend avontuur.", glow: C.magenta },
  { icon: Target, title: "Adaptief leren", desc: "We herkennen zwakke punten en oefenen er gericht mee.", glow: C.indigo },
  { icon: Trophy, title: "Levels & badges", desc: "Munten, XP, levels en badges houden je kind gefocust.", glow: C.green },
  { icon: BarChart3, title: "Inzicht voor ouders", desc: "Volg de voortgang met heldere grafieken.", glow: C.cyan },
  { icon: ShieldCheck, title: "Veilig & Nederlands", desc: "Alle content is in het Nederlands en geschikt voor groep 3 t/m 8.", glow: C.magenta },
];

const steps = [
  { n: "01", title: "Maak een account", desc: "Kind- en ouderaccount in een paar tikken." },
  { n: "02", title: "Kies je wereld", desc: "Speel in een neon-arena of een ander thema." },
  { n: "03", title: "Start een missie", desc: "Lees of reken en verdien XP en munten." },
  { n: "04", title: "Word een kampioen", desc: "Stijg in level en verbeter je vaardigheden." },
];

const testimonials = [
  { name: "Sanne", role: "moeder van Tijn (8)", text: "Tijn vraagt nu zélf of hij mag oefenen. De grafieken zijn goud waard.", avatar: "👩", glow: C.indigo },
  { name: "Meester Joost", role: "leerkracht groep 3 t/m 8", text: "De adaptieve oefeningen sluiten perfect aan bij mijn klas.", avatar: "👨‍🏫", glow: C.cyan },
  { name: "Lotte", role: "leerling, 9 jaar", text: "Ik spaar munten en heb al 6 badges! Lezen is nu echt een game.", avatar: "🦊", glow: C.magenta },
];

function IntroOverlay() {
  return (
    <motion.div exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden" style={{ background: "#050507" }}>
      <div className="absolute inset-0 neon-grid opacity-50" />
      <div className="glow-orb" style={{ width: 420, height: 420, background: "#00FFD1", top: "20%", left: "30%" }} />
      <div className="glow-orb" style={{ width: 360, height: 360, background: "#FF2EA6", bottom: "15%", right: "25%" }} />
      <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }} className="relative text-center">
        <motion.div initial={{ rotate: -12, scale: 0.7 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
          className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center box-glow-cyan mb-6" style={{ background: "linear-gradient(135deg,#00FFD1,#6366F1)" }}>
          <BookOpen className="w-10 h-10 text-black" strokeWidth={2.5} />
        </motion.div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Slim<span className="neon-gradient-text">Avontuur</span></h1>
        <p className="mt-3 font-mono text-sm text-[#00FFD1]/80">avontuur laden…</p>
        <div className="mt-5 w-64 sm:w-72 h-2.5 rounded-full overflow-hidden mx-auto" style={{ background: "rgba(255,255,255,0.08)" }}>
          <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1.5, ease: "easeInOut" }} className="h-full box-glow-cyan" style={{ background: "linear-gradient(90deg,#00FFD1,#FF2EA6)" }} />
        </div>
        <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55 }} className="mt-5 font-display font-black text-2xl text-glow-magenta" style={{ color: "#FF2EA6" }}>LEVEL 1 — START!</motion.p>
      </motion.div>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(() => typeof window !== "undefined" && !sessionStorage.getItem("la_intro_seen"));
  useEffect(() => {
    if (!showIntro) return;
    const t = setTimeout(() => { setShowIntro(false); sessionStorage.setItem("la_intro_seen", "1"); }, 2200);
    return () => clearTimeout(t);
  }, [showIntro]);
  return (
    <div className="min-h-screen font-outfit text-white relative overflow-x-hidden" style={{ background: "#050507" }}>
      <AnimatePresence>{showIntro && <IntroOverlay />}</AnimatePresence>
      {/* Decorative glow orbs */}
      <div className="glow-orb" style={{ width: 520, height: 520, top: -120, left: -120, background: C.cyan }} />
      <div className="glow-orb" style={{ width: 480, height: 480, top: 200, right: -160, background: C.magenta }} />
      <div className="absolute inset-0 neon-grid opacity-60 pointer-events-none" style={{ zIndex: 0 }} />

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/10" style={{ background: "rgba(10,10,15,0.8)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <NeonLogo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-300">
            <a href="#features" className="hover:text-[#00FFD1] transition-colors">Functies</a>
            <a href="#hoe" className="hover:text-[#00FFD1] transition-colors">Hoe het werkt</a>
            <a href="#ouders" className="hover:text-[#00FFD1] transition-colors">Voor ouders</a>
            <Link to="/prijzen" className="hover:text-[#00FFD1] transition-colors">Prijzen</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="rounded-full font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => navigate("/login")} data-testid="nav-login-btn">Inloggen</Button>
            <Button className="rounded-full font-bold text-black box-glow-cyan border-0 hidden sm:flex hover:opacity-90" style={{ background: C.cyan }} onClick={() => navigate("/login")} data-testid="nav-cta-btn">Start gratis</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00FFD1]/40 text-[#00FFD1] font-bold text-sm mb-7 box-glow-cyan" style={{ background: "rgba(0,255,209,0.06)" }}>
              <Sparkles className="w-4 h-4" /> Slimme oefeningen voor groep 3 t/m 8
            </span>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[0.95]">
              Word een<br /><span className="neon-gradient-text">Leerkampioen</span>
            </h1>
            <div className="mt-5 font-mono text-sm sm:text-base">
              <span className="text-slate-500">{"> oefen vandaag: "}</span>
              <Typewriter words={["begrijpend lezen", "rekenen", "verhaaltjessommen", "breuken", "een leerkampioen worden"]} className="text-[#00FFD1] text-glow-cyan font-semibold" />
            </div>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed">
              Oefen begrijpend lezen én rekenen met onbeperkte, slimme missies die meegroeien met jouw kind. Speels, persoonlijk en in het Nederlands.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button className="rounded-full font-bold text-lg py-6 px-8 text-black box-glow-cyan border-0 active:scale-95 hover:opacity-90" style={{ background: C.cyan }} onClick={() => navigate("/login")} data-testid="hero-start-btn">
                Start gratis <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
              <Button variant="outline" className="rounded-full font-bold text-lg py-6 px-8 bg-transparent text-white border-2 active:scale-95 box-glow-magenta hover:bg-white/5" style={{ borderColor: C.magenta }} onClick={() => navigate("/login")} data-testid="hero-demo-btn">
                <Play className="w-4 h-4 mr-2" /> Bekijk voorbeeld
              </Button>
            </div>
            <div className="mt-9 flex items-center gap-6 text-sm font-semibold text-slate-400">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#39FF14]" /> Geen creditcard nodig</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#39FF14]" /> Direct beginnen</span>
            </div>
          </motion.div>

          {/* Hero visual: glowing exercise window */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
            <div className="relative rounded-3xl border border-white/10 overflow-hidden box-glow-indigo" style={{ background: "linear-gradient(160deg,#0C0C14,#0A0A0F)" }}>
              <div className="h-11 flex items-center gap-2 px-4 border-b border-white/10">
                <span className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: "#FEBC2E" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
                <span className="ml-3 text-xs font-mono text-slate-500">missie · begrijpend lezen</span>
              </div>
              <div className="p-6 relative">
                <div className="absolute left-0 right-0 h-16 animate-scan pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,255,209,0.10), transparent)" }} />
                <p className="text-xs font-bold uppercase tracking-widest text-[#00FFD1] mb-2">De slimme octopus</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-5">De octopus heeft acht armen en kan van kleur veranderen om zich te verstoppen voor vijanden…</p>
                <p className="font-bold mb-3">Waarom verandert de octopus van kleur?</p>
                <div className="space-y-2.5">
                  {[["A", "Om mooi te zijn", false], ["B", "Om zich te verstoppen", true], ["C", "Om te slapen", false]].map(([k, t, ok]) => (
                    <div key={k} className="flex items-center gap-3 px-4 py-3 rounded-2xl border text-sm font-semibold" style={ok ? { borderColor: C.cyan, background: "rgba(0,255,209,0.08)", boxShadow: "0 0 16px rgba(0,255,209,.25)" } : { borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={ok ? { background: C.cyan, color: "#000" } : { background: "rgba(255,255,255,0.08)" }}>{k}</span>
                      <span className={ok ? "text-white" : "text-slate-400"}>{t}</span>
                      {ok && <Check className="w-4 h-4 ml-auto text-[#00FFD1]" strokeWidth={3} />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 4 }} className="absolute -left-5 top-8 rounded-2xl border border-white/10 p-3 flex items-center gap-2 box-glow-magenta" style={{ background: "#0A0A0F" }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,46,166,0.15)" }}><Flame className="w-5 h-5" style={{ color: C.magenta }} /></div>
              <div><p className="text-xs text-slate-500 font-semibold">Streak</p><p className="font-display font-extrabold">7 dagen</p></div>
            </motion.div>
            <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 4, delay: 1 }} className="absolute -right-4 bottom-10 rounded-2xl border border-white/10 p-3 flex items-center gap-2 box-glow-cyan" style={{ background: "#0A0A0F" }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,255,209,0.15)" }}><Coins className="w-5 h-5" style={{ color: C.cyan }} /></div>
              <div><p className="text-xs text-slate-500 font-semibold">Munten</p><p className="font-display font-extrabold">+145</p></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative z-10 border-y border-white/10" style={{ background: "#0A0A0F" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-white/5">
          {[["∞", "Unieke missies"], ["2", "Vakken: lezen & rekenen"], ["4", "Niveaus"], ["100%", "Nederlands"]].map(([k, v]) => (
            <div key={v}><p className="font-display text-3xl sm:text-4xl font-black neon-gradient-text">{k}</p><p className="text-sm font-semibold text-slate-400 mt-2">{v}</p></div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Alles om beter te <span className="text-glow-cyan" style={{ color: C.cyan }}>leren</span></h2>
          <p className="mt-4 text-lg text-slate-400">Een complete leeromgeving die kinderen motiveert en ouders gerust stelt.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="group rounded-3xl border border-white/10 p-7 transition-all hover:-translate-y-1" style={{ background: "#0A0A0F" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = f.glow; e.currentTarget.style.boxShadow = `0 0 28px ${f.glow}40`; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.boxShadow = "none"; }}
              data-testid={`feature-${i}`}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${f.glow}1A`, boxShadow: `0 0 18px ${f.glow}55` }}>
                <f.icon className="w-7 h-7" strokeWidth={2.5} style={{ color: f.glow }} />
              </div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="mt-2 text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="hoe" className="relative z-10" style={{ background: "#0A0A0F" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Zo werkt het</h2>
            <p className="mt-4 text-lg text-slate-400">In vier stappen op weg naar leesplezier.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div key={s.n} className="rounded-3xl border border-white/10 p-7" style={{ background: "#050507" }} data-testid={`step-${i}`}>
                <div className="font-display text-4xl font-black mb-4 text-glow-magenta" style={{ color: C.magenta }}>{s.n}</div>
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-slate-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parent preview (calmer deep navy) */}
      <section id="ouders" className="relative z-10" style={{ background: "#070B18" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-3 rounded-[2rem] blur-2xl opacity-40" style={{ background: "linear-gradient(120deg,#6366F1,#00FFD1)" }} />
            <img src={PARENT_IMG} alt="Ouder en kind achter tablet" className="relative rounded-[1.8rem] border border-white/10 w-full object-cover aspect-[4/3]" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#6366F1]/40 text-[#A5B4FC] font-bold text-sm mb-5" style={{ background: "rgba(99,102,241,0.08)" }}><ShieldCheck className="w-4 h-4" /> Voor ouders</span>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Rustig inzicht in de voortgang</h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed">Een helder, betrouwbaar ouderdashboard: leesniveau, sterke en zwakke vaardigheden en wekelijkse trends — voor lezen én rekenen.</p>
            <ul className="mt-6 space-y-3">
              {["Leesniveau & voortgang over tijd", "Sterke vs zwakke vaardigheden per vak", "Wekelijkse en maandelijkse grafieken", "Persoonlijke oefenadviezen"].map((t) => (
                <li key={t} className="flex items-center gap-3 font-semibold text-slate-200"><div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "rgba(57,255,20,0.15)" }}><Check className="w-4 h-4 text-[#39FF14]" /></div>{t}</li>
              ))}
            </ul>
            <Button className="mt-8 rounded-full font-bold py-6 px-8 text-black box-glow-cyan border-0 hover:opacity-90" style={{ background: C.cyan }} onClick={() => navigate("/login")} data-testid="parent-cta-btn">Bekijk het ouderdashboard</Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-center mb-16">Geliefd door kinderen, ouders <span className="neon-gradient-text">én juffen</span></h2>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="rounded-3xl border border-white/10 p-7" style={{ background: "#0A0A0F", boxShadow: `0 0 30px ${t.glow}20` }} data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5" style={{ fill: t.glow, color: t.glow }} />)}</div>
              <p className="text-slate-200 leading-relaxed">"{t.text}"</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-2xl border border-white/10" style={{ background: "#111118" }}>{t.avatar}</div>
                <div><p className="font-display font-bold">{t.name}</p><p className="text-sm text-slate-500">{t.role}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 px-8 py-16 text-center box-glow-indigo" style={{ background: "linear-gradient(135deg,#0C0C18,#0A0A0F)" }}>
          <div className="glow-orb" style={{ width: 300, height: 300, top: -80, left: "40%", background: C.magenta }} />
          <Rocket className="w-16 h-16 mx-auto mb-6 animate-float relative" style={{ color: C.cyan }} />
          <h2 className="relative font-display text-4xl sm:text-5xl font-black tracking-tight">Klaar voor het <span className="neon-gradient-text">avontuur?</span></h2>
          <p className="relative mt-4 text-lg text-slate-300 max-w-xl mx-auto">Begin vandaag gratis en zie je kind groeien tot een echte kampioen.</p>
          <Button className="relative mt-8 rounded-full font-bold text-lg py-6 px-10 text-black box-glow-cyan border-0 active:scale-95 hover:opacity-90" style={{ background: C.cyan }} onClick={() => navigate("/login")} data-testid="cta-bottom-btn">Start nu gratis <ArrowRight className="w-5 h-5 ml-1" /></Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10" style={{ background: "#050507" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <NeonLogo />
            <p className="mt-4 text-slate-500 max-w-sm">Slimme, adaptieve oefeningen voor begrijpend lezen én rekenen voor kinderen in groep 3 t/m 8. Gemaakt in Nederland.</p>
          </div>
          <div>
            <p className="font-display font-bold mb-3">Product</p>
            <ul className="space-y-2 text-slate-500 text-sm">
              <li><a href="#features" className="hover:text-[#00FFD1]">Functies</a></li>
              <li><Link to="/prijzen" className="hover:text-[#00FFD1]">Prijzen</Link></li>
              <li><Link to="/login" className="hover:text-[#00FFD1]">Inloggen</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-display font-bold mb-3">Voor wie</p>
            <ul className="space-y-2 text-slate-500 text-sm">
              <li><a href="#ouders" className="hover:text-[#00FFD1]">Ouders</a></li>
              <li>Kinderen groep 3 t/m 8</li>
              <li>Leerkrachten</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-sm text-slate-600">© 2026 SlimAvontuur. Alle rechten voorbehouden.</div>
      </footer>
    </div>
  );
}
